import { type ReactNode } from 'react';
import { Badge, Tooltip, type BadgeProps } from '@mantine/core';
import { brandColors } from '../../config/theme';
import { humanizePlaceholderToken } from '../../utils/humanizePlaceholderToken';
import styles from './PlaceholderBadge.module.css';

export interface PlaceholderBadgeProps {
  /**
   * Canonical token name, e.g. `RECIPIENT_NAME`. Used in `title` when the badge
   * is not given custom `children` text.
   */
  name: string;
  /**
   * Tooltip / describe text (e.g. registry `description` or the raw `{{...}}` token
   * for unknown placeholders).
   */
  title: string;
  /**
   * Optional second line in the tooltip (registry example value).
   */
  example?: string;
  /**
   * Optional visible label. Defaults to a concise label derived from `name`.
   */
  children?: ReactNode;
  /** Optional Mantine badge props. */
  badgeProps?: Partial<BadgeProps>;
}

/**
 * Small pill used inline where a `{{PLACEHOLDER}}` is shown without a resolved
 * value, so the user still sees a friendly token reference with optional
 * context from the registry. Visual height is defined in
 * `PlaceholderBadge.module.css`. Registry reference chips in
 * `PlaceholderHelp` use the separate compact `registryPill` classes.
 */
export function PlaceholderBadge({
  name,
  title,
  example,
  children,
  badgeProps,
}: PlaceholderBadgeProps) {
  const { classNames: userClassNames, style: userStyle, ...restBadge } =
    badgeProps ?? {};
  /**
   * `BadgeProps['classNames']` can be a callback; only merge string classNames
   * from object form (callers do not use the function form today).
   */
  const userClassNamesObject =
    userClassNames != null && typeof userClassNames === 'object' && !Array.isArray(userClassNames)
      ? (userClassNames as Partial<Record<'root' | 'label', string | undefined>>)
      : null;
  const userRoot =
    userClassNamesObject && typeof userClassNamesObject.root === 'string'
      ? userClassNamesObject.root
      : undefined;
  const userLabel =
    userClassNamesObject && typeof userClassNamesObject.label === 'string'
      ? userClassNamesObject.label
      : undefined;
  const mergedRoot = [styles.pill, userRoot]
    .filter((c) => typeof c === 'string' && c.length > 0)
    .join(' ');
  const mergedLabel = [styles.pillLabel, userLabel]
    .filter((c) => typeof c === 'string' && c.length > 0)
    .join(' ');

  const label = children ?? humanizePlaceholderToken(name);
  const tip = example && example.trim() ? (
    <span>
      {title}
      <br />
      <em>Example: {example}</em>
    </span>
  ) : (
    title
  );

  return (
    <Tooltip multiline w={240} withArrow label={tip}>
      <Badge
        size="sm"
        variant="light"
        color={brandColors.neutral}
        tt="none"
        classNames={
          typeof userClassNames === 'function'
            ? userClassNames
            : {
                ...(userClassNamesObject ?? {}),
                root: mergedRoot || styles.pill,
                label: mergedLabel || styles.pillLabel,
              }
        }
        style={userStyle}
        {...restBadge}
      >
        {label}
      </Badge>
    </Tooltip>
  );
}
